import { useTranslation } from "react-i18next";
import { FC, useEffect, useMemo, useState } from "react";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { CustomTextInput } from "../../../components/CustomTextInput/CustomTextInput";
import { CustomTextSelect } from "../../../components/CustomTextSelect/CustomTextSelect";
import {
  useGetAdminPlans,
  useForcePayment,
  useGetCompanySubscription,
} from "../../../api/billing";
import toast from "react-hot-toast";

type Props = {
  open: boolean;
  companie: TCompanie;
  onCancel?: () => void;
  onClose?: () => void;
};

/**
 * "Force payment" (super-admin): manually activate a paid subscription for a
 * company without Stripe checkout - used for offline payments (bank transfer,
 * cheque) or commercial gestures. A note is stored in the audit trail.
 *
 * Two independent choices:
 *  - a SaaS plan (Essential / Performance / Optimization) - or "keep current"
 *    if the company already has an active one;
 *  - an optional AI Layer add-on. The API attaches the add-on to the SaaS
 *    subscription's metadata, so add-ons never overwrite the base plan.
 */
export const ModalForcePayment: FC<Props> = ({
  open,
  companie,
  onCancel,
  onClose,
}) => {
  const { t } = useTranslation();

  const { plans } = useGetAdminPlans(open);
  const { subscription: currentSub, isPending: loadingSub } =
    useGetCompanySubscription(companie.id, open);

  const saasPlans = useMemo(
    () => plans.filter((p) => p.tier === "saas"),
    [plans]
  );
  const aiAddons = useMemo(
    () => plans.filter((p) => p.tier === "ai_addon"),
    [plans]
  );

  const hasActiveSaas =
    !!currentSub &&
    ["active", "trialing"].includes(currentSub.status) &&
    (currentSub.plan?.tier ?? "saas") === "saas";

  const keepCurrentLabel = t("billing.keepCurrentPlan", "â€” Keep current plan â€”");
  const noAddonLabel = t("billing.noAddon", "â€” No AI add-on â€”");

  const planOptions = useMemo(() => {
    const base = saasPlans.map((p) => p.name);
    return hasActiveSaas ? [keepCurrentLabel, ...base] : base;
  }, [saasPlans, hasActiveSaas, keepCurrentLabel]);

  const addonOptions = useMemo(
    () => [noAddonLabel, ...aiAddons.map((p) => p.name)],
    [aiAddons, noAddonLabel]
  );

  const [planLabel, setPlanLabel] = useState<string>("");
  const [addonLabel, setAddonLabel] = useState<string>("");
  const [cycleLabel, setCycleLabel] = useState<string>("");
  const [vehicleQuantity, setVehicleQuantity] = useState<string>("0");
  /*
   * La duree se compte en JOURS, plus en mois.
   *
   * Le mois etait le seul grain disponible, ce qui rendait impossible les cas
   * les plus frequents cote commercial : prolonger un essai d'une semaine,
   * couvrir les dix jours entre deux virements, offrir quinze jours apres un
   * incident. Il fallait arrondir a un mois entier, donc donner plus que prevu.
   *
   * Defaut a 30 jours : l'equivalent de l'ancien defaut d'un mois, pour que le
   * geste habituel reste a portee sans resaisie.
   */
  const [durationDays, setDurationDays] = useState<string>("30");
  const [note, setNote] = useState<string>("");

  const cycleOptions = [
    t("billing.monthly", "Monthly"),
    t("billing.annual", "Annual"),
  ];

  useEffect(() => {
    if (!open) {
      setPlanLabel("");
      setAddonLabel("");
      setCycleLabel("");
      setVehicleQuantity("0");
      setDurationDays("30");
      setNote("");
    }
  }, [open]);

  const { mutate: forcePayment, isPending } = useForcePayment(onClose);

  const onSave = () => {
    const keepCurrent = hasActiveSaas && planLabel === keepCurrentLabel;
    const selectedPlan = saasPlans.find((p) => p.name === planLabel) ?? null;
    const selectedAddon = aiAddons.find((p) => p.name === addonLabel) ?? null;

    if (!keepCurrent && !selectedPlan) {
      toast.error(t("billing.selectPlan", "Please select a plan"));
      return;
    }

    if (keepCurrent && !selectedAddon) {
      toast.error(
        t(
          "billing.selectAddonOrPlan",
          "Select an AI add-on, or choose a plan to change it"
        )
      );
      return;
    }

    const duration = Number(durationDays);
    if (!keepCurrent && (!Number.isFinite(duration) || duration < 1)) {
      toast.error(
        t("billing.invalidDuration", "La duree doit etre d au moins 1 jour")
      );
      return;
    }

    if (isPending) return;

    if (keepCurrent && selectedAddon) {
      // Add-on only: the API attaches it to the existing SaaS subscription.
      forcePayment({
        company_id: companie.id,
        plan_id: selectedAddon.id,
        billing_cycle:
          cycleLabel === t("billing.annual", "Annual") ? "annual" : "monthly",
        note,
      });
      return;
    }

    forcePayment({
      company_id: companie.id,
      plan_id: selectedPlan!.id,
      ai_addon_id: selectedAddon?.id ?? null,
      billing_cycle:
        cycleLabel === t("billing.annual", "Annual") ? "annual" : "monthly",
      vehicle_quantity: Math.max(0, Number(vehicleQuantity) || 0),
      duration_days: duration,
      note,
    });
  };

  const currentAddonSlug = currentSub?.metadata?.ai_addon_slug ?? null;
  const currentAddonName =
    aiAddons.find((p) => p.slug === currentAddonSlug)?.name ?? currentAddonSlug;

  const periodEnd = currentSub?.current_period_end
    ? new Date(currentSub.current_period_end).toLocaleDateString()
    : null;

  const currentSummary = loadingSub
    ? t("common.loading", "Loading...")
    : currentSub
    ? `${currentSub.plan?.name ?? `#${currentSub.plan_id}`} Â· ${
        currentSub.status
      }${periodEnd ? ` Â· ${t("billing.until", "until")} ${periodEnd}` : ""}${
        currentAddonName ? ` Â· + ${currentAddonName}` : ""
      }`
    : t("billing.noSubscription", "No subscription");

  return (
    <CustomModal
      title={`${t("billing.forcePayment", "Force payment")} - ${
        companie.name
      }`}
      open={open}
      onCancel={onCancel}
      onSave={onSave}
    >
      <div
        style={{
          padding: "10px 12px",
          marginBottom: 16,
          borderRadius: 8,
          backgroundColor: "#F5F6FA",
          fontSize: 13,
        }}
      >
        <strong>
          {t("billing.currentSubscription", "Current subscription")}:
        </strong>{" "}
        {currentSummary}
      </div>
      <CustomTextSelect
        label={t("billing.plan", "Plan")}
        placeholder={t("billing.selectPlan", "Please select a plan")}
        options={planOptions}
        value={planLabel}
        onChange={(e: any) => setPlanLabel(String(e.target.value))}
        marginBottom={16}
      />
      <CustomTextSelect
        label={t("billing.aiAddon", "AI Layer add-on (optional)")}
        placeholder={noAddonLabel}
        options={addonOptions}
        value={addonLabel}
        onChange={(e: any) => setAddonLabel(String(e.target.value))}
        marginBottom={16}
      />
      <CustomTextSelect
        label={t("billing.billingCycle", "Billing cycle")}
        placeholder={t("billing.billingCycle", "Billing cycle")}
        options={cycleOptions}
        value={cycleLabel}
        onChange={(e: any) => setCycleLabel(String(e.target.value))}
        marginBottom={16}
      />
      <CustomTextInput
        label={t("billing.durationDays", "Duree (jours)")}
        value={durationDays}
        onChange={(e) => setDurationDays(e.target.value)}
        marginBottom={16}
      />
      <CustomTextInput
        label={t(
          "billing.vehicleQuantity",
          "Vehicle quantity (0 = plan default)"
        )}
        value={vehicleQuantity}
        onChange={(e) => setVehicleQuantity(e.target.value)}
        marginBottom={16}
      />
      <CustomTextInput
        label={t("billing.note", "Note (payment reference, reason...)")}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        marginBottom={16}
      />
    </CustomModal>
  );
};
